import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/utils/formatting";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

// Define the structure based on change_log table
type ChangeLogEntry = {
  id: number;
  version_id: number;
  provider_id: number;
  region_id: number;
  entity_type: string;
  entity_id: number;
  field_name: string;
  action_type: 'CREATE' | 'UPDATE' | 'DELETE';
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  updated_at: string;
};

interface PlanChangesTableProps {
  changes?: ChangeLogEntry[];
  isLoading: boolean;
}


export function PlanChangesTable({ changes, isLoading }: PlanChangesTableProps) {
  const [planNames, setPlanNames] = useState<Record<number, string>>({});

  // Filter to only show plans entity_type
  const planChanges = changes?.filter(change =>
    change.entity_type === 'plans'
  ) || [];

  // Get unique plan IDs
  const planIds = [...new Set(planChanges.map(c => c.entity_id))];

  // Fetch plan names from database and extract from deleted entity data
  useEffect(() => {
    const fetchPlanNames = async () => {
      if (planIds.length === 0) return;

      // First try to get names from the plans table for existing plans
      const { data, error } = await supabase
        .from('plans')
        .select('id, name')
        .in('id', planIds);

      const nameMap: Record<number, string> = {};

      // Add names for existing plans
      if (!error && data) {
        data.forEach(plan => {
          nameMap[plan.id] = plan.name;
        });
      }

      // For plans not found in the database (deleted), extract names from change log old_value
      const missingPlanIds = planIds.filter(id => !nameMap[id]);
      if (missingPlanIds.length > 0) {
        missingPlanIds.forEach(planId => {
          // Find a change entry for this plan that has old_value with entity data
          const entityChange = planChanges.find(change =>
            change.entity_id === planId &&
            change.field_name === 'entity' &&
            change.old_value
          );

          if (entityChange?.old_value) {
            try {
              const entityData = JSON.parse(entityChange.old_value);
              nameMap[planId] = entityData.name || `Plan #${planId}`;
            } catch (error) {
              console.error('Failed to parse entity data for plan:', planId, error);
              nameMap[planId] = `Plan #${planId}`;
            }
          } else {
            nameMap[planId] = `Plan #${planId}`;
          }
        });
      }

      setPlanNames(nameMap);
    };

    fetchPlanNames();
  }, [planIds.join(','), planChanges]);

  // Group changes by plan and then by actual action type
  const groupedByPlan = planChanges.reduce((acc, change) => {
    const planId = change.entity_id;

    if (!acc[planId]) {
      acc[planId] = {
        planId,
        planName: planNames[planId] || `Plan #${planId}`,
        createFields: {},
        updateFields: {},
        deleteFields: {},
        createTimestamp: null,
        updateTimestamp: null,
        deleteTimestamp: null,
        latestTimestamp: change.created_at
      };
    }

    // Track latest timestamp for this plan
    if (new Date(change.created_at) > new Date(acc[planId].latestTimestamp)) {
      acc[planId].latestTimestamp = change.created_at;
    }

    // Handle entity-level operations (whole plan created/deleted)
    if (change.field_name === 'entity') {
      if (change.old_value === null && change.new_value !== null) {
        // Entire plan was created
        acc[planId].isEntityCreated = true;
        acc[planId].entityCreatedAt = change.created_at;
        // Parse entity data for display
        try {
          const entityData = JSON.parse(change.new_value);
          acc[planId].entityData = entityData;
        } catch (error) {
          console.error('Failed to parse entity creation data:', error);
        }
      } else if (change.old_value !== null && change.new_value === null) {
        // Entire plan was deleted
        acc[planId].isEntityDeleted = true;
        acc[planId].entityDeletedAt = change.created_at;
        // Parse entity data for display
        try {
          const entityData = JSON.parse(change.old_value);
          acc[planId].entityData = entityData;
        } catch (error) {
          console.error('Failed to parse entity deletion data:', error);
        }
      }
    } else {
      // Handle field-level changes
      if (change.old_value === null && change.new_value !== null) {
        // Field was created (added)
        acc[planId].createFields[change.field_name] = {
          value: change.new_value
        };
        if (!acc[planId].createTimestamp || new Date(change.created_at) > new Date(acc[planId].createTimestamp!)) {
          acc[planId].createTimestamp = change.created_at;
        }
      } else if (change.old_value !== null && change.new_value === null) {
        // Field was deleted (removed)
        acc[planId].deleteFields[change.field_name] = {
          value: change.old_value
        };
        if (!acc[planId].deleteTimestamp || new Date(change.created_at) > new Date(acc[planId].deleteTimestamp!)) {
          acc[planId].deleteTimestamp = change.created_at;
        }
      } else if (change.old_value !== null && change.new_value !== null) {
        // Field was updated
        if (!acc[planId].updateFields[change.field_name]) {
          acc[planId].updateFields[change.field_name] = {
            oldValue: change.old_value,
            newValue: change.new_value
          };
        } else {
          // If field was already updated, keep the original old value but update the new value
          acc[planId].updateFields[change.field_name].newValue = change.new_value;
        }
        if (!acc[planId].updateTimestamp || new Date(change.created_at) > new Date(acc[planId].updateTimestamp!)) {
          acc[planId].updateTimestamp = change.created_at;
        }
      }
    }

    return acc;
  }, {} as Record<number, {
    planId: number;
    planName: string;
    createFields: Record<string, { value: string }>;
    updateFields: Record<string, { oldValue: string; newValue: string }>;
    deleteFields: Record<string, { value: string }>;
    createTimestamp: string | null;
    updateTimestamp: string | null;
    deleteTimestamp: string | null;
    latestTimestamp: string;
    isEntityCreated?: boolean;
    isEntityDeleted?: boolean;
    entityCreatedAt?: string;
    entityDeletedAt?: string;
    entityData?: any;
  }>);

  if (isLoading) return <div>Loading...</div>;

  // Convert to array and sort
  const sortedPlans = Object.values(groupedByPlan).sort((a, b) =>
    new Date(b.latestTimestamp).getTime() - new Date(a.latestTimestamp).getTime()
  );

  const renderFieldValue = (
    actionType: 'CREATE' | 'UPDATE' | 'DELETE',
    _fieldName: string,
    fieldData: any
  ) => {
    if (!fieldData) return <span className="text-muted-foreground">-</span>;

    // Regular field handling
    if (actionType === 'CREATE') {
      return (
        <div className="text-xs text-green-600 font-medium">
          {fieldData.value || 'null'}
        </div>
      );
    } else if (actionType === 'UPDATE') {
      return (
        <div className="space-y-1">
          <div className="text-xs text-red-600 line-through">
            {fieldData.oldValue || 'null'}
          </div>
          <div className="text-xs text-green-600 font-medium">
            {fieldData.newValue || 'null'}
          </div>
        </div>
      );
    } else if (actionType === 'DELETE') {
      return (
        <div className="text-xs text-red-600 line-through">
          {fieldData.value || 'null'}
        </div>
      );
    }

    return <span className="text-muted-foreground">-</span>;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Plan Name</TableHead>
          <TableHead className="font-semibold">Action</TableHead>
          <TableHead className="font-semibold">Name</TableHead>
          <TableHead className="font-semibold">Price</TableHead>
          <TableHead className="font-semibold">Description</TableHead>
          <TableHead className="font-semibold">Color Index</TableHead>
          <TableHead className="font-semibold">Sort Order</TableHead>
          <TableHead className="font-semibold">Timestamp</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedPlans.map((plan) => {
          // Build array of actions to display
          const actions = [];

          // Add ENTITY_CREATED row if the entire plan was created
          if (plan.isEntityCreated) {
            actions.push({
              type: 'ENTITY_CREATED' as const,
              fields: {},
              timestamp: plan.entityCreatedAt,
              entityData: plan.entityData
            });
          }

          // Add ENTITY_DELETED row if the entire plan was deleted
          if (plan.isEntityDeleted) {
            actions.push({
              type: 'ENTITY_DELETED' as const,
              fields: {},
              timestamp: plan.entityDeletedAt,
              entityData: plan.entityData
            });
          }

          // Add CREATE row if there are created fields
          if (Object.keys(plan.createFields).length > 0) {
            actions.push({
              type: 'CREATE' as const,
              fields: plan.createFields,
              timestamp: plan.createTimestamp
            });
          }

          // Add UPDATE row if there are updated fields
          if (Object.keys(plan.updateFields).length > 0) {
            actions.push({
              type: 'UPDATE' as const,
              fields: plan.updateFields,
              timestamp: plan.updateTimestamp
            });
          }

          // Add DELETE row if there are deleted fields
          if (Object.keys(plan.deleteFields).length > 0) {
            actions.push({
              type: 'DELETE' as const,
              fields: plan.deleteFields,
              timestamp: plan.deleteTimestamp
            });
          }

          if (actions.length === 0) return null;

          return actions.map((action, index) => {
            const isFirstRow = index === 0;

            return (
              <TableRow key={`${plan.planId}-${action.type}`} className="hover:bg-muted/50">
                {/* Plan Name - merged cell for first row only */}
                {isFirstRow && (
                  <TableCell
                    rowSpan={actions.length}
                    className="font-medium align-top border-r"
                  >
                    <div className="font-semibold">{plan.planName}</div>
                  </TableCell>
                )}

                {/* Action */}
                <TableCell>
                  <Badge className={
                    action.type === 'ENTITY_CREATED' ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100 font-bold" :
                    action.type === 'ENTITY_DELETED' ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 font-bold" :
                    action.type === 'CREATE' ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" :
                    action.type === 'UPDATE' ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100" :
                    "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
                  }>
                    {action.type === 'ENTITY_CREATED' ? 'PLAN CREATED' :
                     action.type === 'ENTITY_DELETED' ? 'PLAN DELETED' :
                     action.type}
                  </Badge>
                </TableCell>

                {/* Name */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs font-medium ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                        {action.entityData?.name || '-'}
                      </div>
                    : renderFieldValue(action.type, 'name', action.fields['name'])}
                </TableCell>

                {/* Price */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                        {action.entityData?.monthly_price ? `$${action.entityData.monthly_price}` : '-'}
                      </div>
                    : renderFieldValue(action.type, 'price', action.fields['price'])}
                </TableCell>

                {/* Description */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'} max-w-xs truncate`}>
                        {action.entityData?.description || '-'}
                      </div>
                    : renderFieldValue(action.type, 'description', action.fields['description'])}
                </TableCell>

                {/* Color Index */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                        {action.entityData?.color_index || '-'}
                      </div>
                    : renderFieldValue(action.type, 'color_index', action.fields['color_index'])}
                </TableCell>

                {/* Sort Order */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                        {action.entityData?.sort_order || '-'}
                      </div>
                    : renderFieldValue(action.type, 'sort_order', action.fields['sort_order'])}
                </TableCell>

                {/* Timestamp */}
                <TableCell>
                  <div className="text-xs">{action.timestamp ? formatDateTime(action.timestamp) : '-'}</div>
                </TableCell>
              </TableRow>
            );
          });
        })}
      </TableBody>
    </Table>
  );
}